/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * The screens' handle on the shopping engine, plus the two small pieces of
 * UI every shopping screen shares: the live indicator and the undo bar.
 */

import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";

import type { ShoppingOp } from "../../../api/shoppingLive.ts";
import { useToast } from "../../../components/Toast.tsx";
import { shoppingEngine, type Snapshot } from "./engine.ts";
import { applyOp, inverseOf, type Model } from "./model.ts";

export type NewOp = Omit<ShoppingOp, "id" | "clientTs">;

export function useShoppingLive(): Snapshot & { submit: (ops: NewOp[]) => void } {
  const snap = useSyncExternalStore(shoppingEngine.subscribe, shoppingEngine.getSnapshot);
  const toast = useToast();
  useEffect(() => {
    shoppingEngine.setRejectionHandler((m) => toast(m, "danger"));
    return () => shoppingEngine.setRejectionHandler(null);
  }, [toast]);
  const submit = useCallback((ops: NewOp[]) => { shoppingEngine.submit(ops); }, []);
  return { ...snap, submit };
}

/**
 * Run operations with an 8-second "Undo" offer. The way back is worked out
 * from the view as it is NOW, before the change, so it restores exactly what
 * was there — including a quantity a duplicate merged into.
 */
export function useUndoable(model: Model | null) {
  const [offer, setOffer] = useState<{ label: string; ops: NewOp[]; key: number } | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  const run = useCallback((ops: NewOp[], label: string | null) => {
    const now = new Date().toISOString();
    const inverse: NewOp[] = [];
    let m = model;
    for (const op of ops) {
      if (m) {
        inverse.unshift(...inverseOf(m, { ...op, id: "" } as ShoppingOp, () => crypto.randomUUID()));
        m = applyOp(m, { ...op, id: "" } as ShoppingOp, now);
      }
    }
    shoppingEngine.submit(ops);
    if (!label || !inverse.length) return;
    if (timer.current) clearTimeout(timer.current);
    setOffer({ label, ops: inverse, key: Date.now() });
    timer.current = setTimeout(() => setOffer(null), 8000);
  }, [model]);

  const undo = useCallback(() => {
    if (!offer) return;
    shoppingEngine.submit(offer.ops);
    setOffer(null);
  }, [offer]);

  const bar = offer ? (
    <div key={offer.key}
         className="fixed inset-x-0 bottom-4 z-40 flex justify-center px-4 animate-fade-in"
         role="status" aria-live="polite">
      <div className="flex items-center gap-3 rounded-xl border border-rule bg-ink px-4 py-2.5 text-sm text-paper shadow-soft">
        <span>{offer.label}</span>
        <button className="font-semibold text-paper underline underline-offset-2"
                onClick={undo}>
          Undo
        </button>
      </div>
    </div>
  ) : null;

  return { run, bar };
}

export function LiveBadge({ status, unsent }: { status: Snapshot["status"]; unsent: number }) {
  const label = status === "live" ? (unsent ? "Saving…" : "Live")
    : status === "offline" ? `Offline${unsent ? ` — ${unsent} change${unsent === 1 ? "" : "s"} will sync` : ""}`
    : status === "error" ? "Not connected"
    : "Connecting…";
  const dot = status === "live" ? "bg-ok" : status === "offline" || status === "error" ? "bg-warn" : "bg-ink-3";
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-ink-3" title="Changes from your family appear here as they happen">
      <span className={`h-2 w-2 rounded-full ${dot}`} aria-hidden />
      {label}
    </span>
  );
}
